import { ChangeDetectorRef, Component, Input, Output, EventEmitter, OnChanges, OnDestroy } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Observable, Subscription } from "rxjs";
import { NzModalService } from "ng-zorro-antd/modal";
import { AssessmentTasksService } from "../../services/assessment_tasks.service";
import { EvidenceCommentsService, WorkItemComment } from "../../services/evidence_comments.service";
import { AssessmentTaskSummary, AssessmentTaskPage } from "../../services/assessment_tasks.types";
import { AssessmentWorkItemEvidenceSummary } from "../../services/assessment_work_items.types";
import { LoginService } from "../../services/login.service";
import { AssessmentTaskInvalidationService } from "../../services/assessment_task_invalidation.service";
import { EvidenceRequirementInvalidationService } from "../../services/evidence_requirement_invalidation.service";
import { formatDateTimeDisplay } from "../../utils/date_time_display.util";
interface TaskCommentsState {
    items: WorkItemComment[];
    page: number;
    total: number;
    loading: boolean;
    error: string | null;
    request: Subscription | null;
    version: number;
    newBody: string;
    editingId: number | null;
    editRevision: number | null;
    editBody: string;
    latestBody: string | null;
    editConflict: boolean;
    conflictReload: boolean;
    mutating: boolean;
    mutationError: string | null;
    mutationConflict: boolean;
    mutationRequest: Subscription | null;
}
const COMMENT_PAGE_SIZE=5, COMMENT_MAX_CHARACTERS=10000, COMMENT_MAX_BYTES=40000, COMMENT_MAX_CODE_UNITS=20000;
function commentBodyError(body: string): string | null {
    if (!body.trim()) return "Enter a comment before saving.";
    if (body.length > COMMENT_MAX_CODE_UNITS) return "Comments must be 10,000 characters or fewer.";
    let characters = 0;
    for (const character of body) {
        const point = character.codePointAt(0)!;
        if (point === 0 || point >= 0xd800 && point <= 0xdfff) {
            return "The comment contains an invalid character.";
        }
        if (++characters > COMMENT_MAX_CHARACTERS) return "Comments must be 10,000 characters or fewer.";
    }
    return new TextEncoder().encode(body).length > COMMENT_MAX_BYTES
        ? "The comment is too large to save." : null;
}
@Component({selector: "app-work-item-comments", templateUrl: "./work_item_comments.component.html", styleUrls: ["./work_item_comments.component.scss"]})
export class WorkItemCommentsComponent implements OnChanges, OnDestroy {
    @Input() taskItem: AssessmentTaskSummary | null = null;
    @Input() evidence: Pick<AssessmentWorkItemEvidenceSummary, "id" | "productLicenseId"> | null = null;
    @Input() busy = false;
    @Output() pendingChange = new EventEmitter<boolean>();
    readonly comments = new Map<number | string, TaskCommentsState>();
    readonly commentMaxLength = COMMENT_MAX_CODE_UNITS;
    readonly formatDateTime = formatDateTimeDisplay;
    readonly state = { isOpen: true };
    private scopeVersion = 0;
    private owner = "";
    private session = "";
    private readonly subscriptions = new Subscription();
    get taskRows(): AssessmentTaskSummary[] { return this.taskItem ? [this.taskItem] : []; }
    get evidenceRows(): { evidence: AssessmentWorkItemEvidenceSummary }[] {
        return this.evidence ? [{ evidence: this.evidence as AssessmentWorkItemEvidenceSummary }] : [];
    }
    get target() { return this.taskItem || (this.evidence ? this.evidenceCommentTarget(this.evidence as AssessmentWorkItemEvidenceSummary) : null); }
    constructor(private readonly tasks: AssessmentTasksService, private readonly evidenceComments: EvidenceCommentsService,
        private readonly login: LoginService, private readonly modal: NzModalService,
        invalidations: AssessmentTaskInvalidationService, evidenceInvalidations: EvidenceRequirementInvalidationService,
        private readonly changeDetectorRef: ChangeDetectorRef) {
        this.subscriptions.add(login.getUserUpdates().subscribe(() => {
            if (this.session && this.session !== this.sessionKey()) {
                this.scopeVersion++; this.clearComments(); this.state.isOpen=false; this.changeDetectorRef.markForCheck();
            }
        }));
        this.subscriptions.add(invalidations.watchInvalidations().subscribe(change => {
            if (this.state.isOpen && this.session === this.sessionKey() &&
                (change.productLicenseId === null || change.productLicenseId === (this.taskItem?.productLicenseId ?? this.evidence?.productLicenseId))) this.refreshVisibleComments();
        }));
        this.subscriptions.add(evidenceInvalidations.watchChanges().subscribe(change => {
            if (this.evidence && change.productLicenseId === this.evidence.productLicenseId &&
                this.state.isOpen && this.session === this.sessionKey()) this.refreshVisibleComments();
        }));
    }
    private sessionKey(): string { return JSON.stringify([this.login.getCurrentAccountId(), this.login.getCurrentUserId(), this.login.getToken()]); }
    ngOnChanges(): void {
        const owner = this.taskItem ? "task:"+this.taskItem.id : this.evidence ? "evidence:"+this.evidence.productLicenseId+":"+this.evidence.id : "";
        const session=this.sessionKey();
        if (owner !== this.owner || session !== this.session) { this.scopeVersion++; this.clearComments(); this.owner=owner; this.session=session; this.state.isOpen=!!owner; }
    }
    ngOnDestroy(): void { this.scopeVersion++; this.state.isOpen=false; this.clearComments(); this.subscriptions.unsubscribe(); }
    taskWritePending(key: number | string): boolean { return this.busy || !!this.comments.get(key)?.mutating; }
    evidenceCommentTarget(evidence: AssessmentWorkItemEvidenceSummary) {
        return { id: `evidence:${evidence.productLicenseId}:${evidence.id}`, capabilities: { canAddComment: true } };
    }

    private commentSource(key: number | string): AssessmentTaskSummary | AssessmentWorkItemEvidenceSummary | undefined {
        return typeof key === "number" ? this.taskRows.find(task => task.id === key)
            : this.evidenceRows.map(item => item.evidence).find(item => this.evidenceCommentTarget(item).id === key);
    }

private commentRequest(key: number | string, operation: "list" | "create" | "edit" | "delete",
        body?: string, commentId?: number, revision?: number, page = 1): Observable<any> {
        const source = this.commentSource(key);
        if (!source) throw new Error("Comment item is no longer visible.");
        if (typeof key === "number") {
            const task = source as AssessmentTaskSummary;
            if (operation === "list") return this.tasks.listComments(key, { page, pageSize: COMMENT_PAGE_SIZE });
            if (operation === "create") return this.tasks.addComment(key, body!, task);
            if (operation === "edit") return this.tasks.editComment(key, commentId!, body!, revision!, task);
            return this.tasks.deleteComment(key, commentId!, revision!, task);
        }
        const evidence = source as AssessmentWorkItemEvidenceSummary;
        if (operation === "list") return this.evidenceComments.list(evidence, page);
        return this.evidenceComments.mutate(evidence, operation === "create" ? "POST" : operation === "edit" ? "PATCH" : "DELETE",
            operation === "create" ? { body } : operation === "edit" ? { body, revision } : { revision }, commentId);
    }

    toggleComments(taskId: number | string): void {
        if (this.taskWritePending(taskId)) return;
        if (this.comments.has(taskId)) {
            const panel = this.comments.get(taskId)!;
            if (panel.editingId !== null) return;
            panel.request?.unsubscribe();
            panel.mutationRequest?.unsubscribe();
            this.comments.delete(taskId);
            return;
        }
        if (!this.commentSource(taskId)) return;
        this.comments.set(taskId, {
            items: [], page: 1, total: 0, loading: false, error: null, request: null, version: 0,
            newBody: "", editingId: null, editRevision: null, editBody: "", latestBody: null,
            editConflict: false, conflictReload: false, mutating: false, mutationError: null,
            mutationConflict: false, mutationRequest: null
        });
        this.loadComments(taskId, 1);
    }

    changeCommentPage(taskId: number | string, page: number): void {
        const current = this.comments.get(taskId);
        if (current && !current.loading && !current.mutating && current.editingId === null &&
            Number.isSafeInteger(page) && page > 0 && page !== current.page) {
            this.loadComments(taskId, page);
        }
    }

    isEditingCommentVisible(panel: TaskCommentsState): boolean {
        return panel.editingId === null || panel.items.some((item) => item.id === panel.editingId);
    }

    retryComments(taskId: number | string): void {
        const current = this.comments.get(taskId);
        if (current) this.loadComments(taskId, current.page);
    }

    reloadComments(taskId: number | string | string): void {
        const current = this.comments.get(taskId);
        if (!current) return;
        current.conflictReload = true;
        this.loadComments(taskId, current.page);
    }

    addComment(taskId: number | string): void {
        const panel = this.comments.get(taskId);
        const task = this.commentSource(taskId);
        if (!panel || !task || (typeof taskId === "number" && !(task as AssessmentTaskSummary).capabilities.canAddComment) || this.taskWritePending(taskId) || panel.loading ||
            panel.editingId !== null) return;
        const error = commentBodyError(panel.newBody);
        if (error) {
            panel.mutationError = error;
            return;
        }
        this.mutateComment(taskId, panel, this.commentRequest(taskId, "create", panel.newBody), () => {
            panel.newBody = "";
        });
    }

    startEditComment(taskId: number | string, comment: WorkItemComment): void {
        const panel = this.comments.get(taskId);
        const current = panel?.items.find((item) => item.id === comment.id);
        if (!panel || this.taskWritePending(taskId) || panel.editingId !== null ||
            !current?.capabilities.canEditOwnComment) return;
        panel.editingId = current.id;
        panel.editRevision = current.revision;
        panel.editBody = current.body;
        panel.latestBody = null;
        panel.editConflict = false;
        panel.mutationError = null;
        panel.mutationConflict = false;
    }

    cancelEditComment(taskId: number | string): void {
        const panel = this.comments.get(taskId);
        if (!panel || panel.mutating) return;
        panel.editingId = null;
        panel.editRevision = null;
        panel.editBody = "";
        panel.latestBody = null;
        panel.editConflict = false;
        panel.mutationError = null;
        panel.mutationConflict = false;
    }

    saveEditedComment(taskId: number | string): void {
        const panel = this.comments.get(taskId);
        const comment = panel?.items.find((item) => item.id === panel.editingId);
        if (!panel || this.taskWritePending(taskId) || panel.editConflict ||
            !comment?.capabilities.canEditOwnComment ||
            panel.editRevision === null) return;
        if (comment.revision !== panel.editRevision) {
            panel.editConflict = true;
            panel.mutationConflict = true;
            panel.mutationError = "This comment changed. Reload it before saving your draft.";
            return;
        }
        const error = commentBodyError(panel.editBody);
        if (error) {
            panel.mutationError = error;
            return;
        }
        if (panel.editBody === comment.body) {
            this.cancelEditComment(taskId);
            return;
        }
        this.mutateComment(taskId, panel,
            this.commentRequest(taskId, "edit", panel.editBody, comment.id, panel.editRevision),
            () => this.cancelEditComment(taskId));
    }

    confirmDeleteComment(taskId: number | string, comment: WorkItemComment): void {
        const panel = this.comments.get(taskId);
        const current = panel?.items.find((item) => item.id === comment.id);
        if (!panel || this.taskWritePending(taskId) || panel.editingId !== null ||
            !current?.capabilities.canDeleteOwnComment) return;
        const revision = current.revision;
        this.modal.confirm({
            nzTitle: "Delete comment?",
            nzContent: "This comment will be removed from this item.",
            nzOkText: "Delete",
            nzOkDanger: true,
            nzOnOk: () => {
                const latest = panel.items.find((item) => item.id === comment.id);
                if (!this.state.isOpen || this.comments.get(taskId) !== panel ||
                    this.taskWritePending(taskId) ||
                    !latest?.capabilities.canDeleteOwnComment) return;
                if (latest.revision !== revision) {
                    panel.mutationConflict = true;
                    panel.mutationError = "This comment changed. Reload it before deleting.";
                    return;
                }
                this.mutateComment(taskId, panel, this.commentRequest(taskId, "delete", undefined, comment.id, revision), () => {
                    if (panel.editingId === comment.id) this.cancelEditComment(taskId);
                });
            }
        });
    }

    trackComment(_: number, comment: WorkItemComment): number {
        return comment.id;
    }

    private clearComments(): void {
        for (const comments of this.comments.values()) {
            comments.request?.unsubscribe();
            comments.mutationRequest?.unsubscribe();
        }
        this.comments.clear();
        this.pendingChange.emit(false);
    }

    private refreshVisibleComments(): void {
        for (const [taskId, panel] of this.comments) {
            if (this.commentSource(taskId)) {
                this.loadComments(taskId, panel.page);
            } else {
                panel.request?.unsubscribe();
                panel.mutationRequest?.unsubscribe();
                this.comments.delete(taskId);
            }
        }
    }

    private mutateComment(
        taskId: number | string, panel: TaskCommentsState, request: Observable<unknown>, onSuccess: () => void
    ): void {
        const scope = this.scopeVersion;
        const token = this.login.getToken();
        panel.mutating = true;
        this.changeDetectorRef.markForCheck();
        this.pendingChange.emit(true);
        panel.mutationError = null;
        panel.mutationConflict = false;
        const subscription = request.subscribe({
            next: () => {
                if (scope !== this.scopeVersion || token !== this.login.getToken() ||
                    this.comments.get(taskId) !== panel) return;
                panel.mutating = false;
                this.changeDetectorRef.markForCheck();
                this.pendingChange.emit(false);
                panel.mutationRequest = null;
                onSuccess();
            },
            error: (error: unknown) => {
                if (scope !== this.scopeVersion || token !== this.login.getToken() ||
                    this.comments.get(taskId) !== panel) return;
                panel.mutating = false;
                this.changeDetectorRef.markForCheck();
                this.pendingChange.emit(false);
                panel.mutationRequest = null;
                const conflict = error instanceof HttpErrorResponse && error.error?.code === "TASK_CONFLICT";
                panel.mutationConflict = conflict;
                panel.editConflict = conflict && panel.editingId !== null;
                panel.mutationError = conflict
                    ? "This comment changed. Reload the latest version; your draft is still here."
                    : "Unable to save this comment. Please try again.";
            }
        });
        if (!subscription.closed) panel.mutationRequest = subscription;
    }

    private loadComments(taskId: number | string, page: number): void {
        const comments = this.comments.get(taskId);
        if (!comments || !this.commentSource(taskId)) return;
        comments.request?.unsubscribe();
        const version = ++comments.version;
        const scope = this.scopeVersion;
        const token = this.login.getToken();
        comments.items = [];
        comments.page = page;
        comments.loading = true;
        this.changeDetectorRef.markForCheck();
        comments.error = null;
        comments.request = this.commentRequest(taskId, "list", undefined, undefined, undefined, page).subscribe({
            next: (result: AssessmentTaskPage<WorkItemComment>) => {
                if (scope !== this.scopeVersion || token !== this.login.getToken() || version !== comments.version ||
                    this.comments.get(taskId) !== comments || !this.state.isOpen) return;
                this.changeDetectorRef.markForCheck();
                if (result.page !== page || result.pageSize !== COMMENT_PAGE_SIZE) {
                    comments.error = "Unable to load comments.";
                    comments.loading = false;
                    return;
                }
                const lastPage = Math.max(1, Math.ceil(result.total / COMMENT_PAGE_SIZE));
                if (!result.items.length && page > lastPage) {
                    // Comments may have been removed while an older page was open.
                    this.loadComments(taskId, lastPage);
                    return;
                }
                comments.items = result.items;
                comments.total = result.total;
                comments.loading = false;
                if (comments.conflictReload) {
                    comments.conflictReload = false;
                    if (comments.editingId !== null) {
                        const latest = result.items.find((item) => item.id === comments.editingId);
                        if (latest?.capabilities.canEditOwnComment) {
                            comments.editRevision = latest.revision;
                            comments.latestBody = latest.body;
                            comments.editConflict = false;
                            comments.mutationError = null;
                            comments.mutationConflict = false;
                        }
                    } else {
                        comments.mutationError = null;
                        comments.mutationConflict = false;
                    }
                }
            },
            error: () => {
                if (scope !== this.scopeVersion || token !== this.login.getToken() || version !== comments.version ||
                    this.comments.get(taskId) !== comments) return;
                this.changeDetectorRef.markForCheck();
                comments.loading = false;
                comments.error = "Unable to load comments.";
                comments.request = null;
            },
            complete: () => {
                if (scope === this.scopeVersion && token === this.login.getToken() &&
                    version === comments.version) comments.request = null;
            }
        });
    }
}
